import { useNotificationTemplatesListQuery } from '@client/graphql/__generated__/main-operations';
import {
  ContactType,
  NotificationTemplateFilters,
  NotificationTemplateListItemFragment,
  TransactionSide,
  TransactionStageType,
} from '@client/graphql/__generated__/types';
import { useMemo } from 'react';

interface UseNotificationTemplatesOptions {
  partnerId: string;
  side?: TransactionSide;
  recipient?: ContactType;
  filters?: NotificationTemplateFilters;
  skip?: boolean;
}

export const useNotificationTemplates = ({
  partnerId,
  side,
  recipient,
  filters,
  skip = false,
}: UseNotificationTemplatesOptions) => {
  const { data, loading, error, refetch } = useNotificationTemplatesListQuery({
    variables: {
      partnerId,
      perPage: 100,
      filters: {
        ...filters,
        side,
      },
    },
    skip: skip || !partnerId,
  });

  const templates = useMemo(() => {
    const results = data?.notificationTemplatesList?.results ?? [];

    // Filter by recipient client-side (not supported by GraphQL filters)
    if (recipient) {
      return results.filter((template) =>
        template.recipients.includes(recipient)
      );
    }

    return results;
  }, [data?.notificationTemplatesList?.results, recipient]);

  // Group templates by eventType for Kanban columns
  const templatesByEventType = useMemo(() => {
    return templates.reduce(
      (acc, template) => {
        const eventType = template.eventType;
        if (!acc[eventType]) {
          acc[eventType] = [];
        }
        acc[eventType].push(template);
        // Sort by orderIndex within each group
        acc[eventType].sort((a, b) => a.orderIndex - b.orderIndex);

        return acc;
      },
      {} as Record<TransactionStageType, NotificationTemplateListItemFragment[]>
    );
  }, [templates]);

  return {
    templates,
    templatesByEventType,
    loading,
    error,
    refetch,
  };
};
